"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import Button from "@/components/buttons";
import { useZStore } from "@/utils/z-store";
import { FaMailchimp } from "react-icons/fa";
import ConnectedCheckIcon from "@/assets/icons/connected-icon.svg";
import { setupMailchimp } from "@/app/(dashboard)/dashboard/utils/mailchimp-utils/setup-mailchimp";
import axios from "axios";
import toast from "react-hot-toast";
import cn from "@/utils/cn";
import TextComponent from "../TextComponent";
import { IoCloseCircleOutline } from "react-icons/io5";
import CloseIcon from "./assets/close-icon.svg";

import { FiClock } from "react-icons/fi";
import MailchimpConnectButton from "@/components/MailchimpConnectButton";
import Link from "next/link";

interface MailchimpAPISettingsModalProps {
  reason?: "no-keys" | "no-data";
  initialApiKey?: string;
}

const MailchimpAPISettingsModal = ({ reason, initialApiKey }: MailchimpAPISettingsModalProps) => {
  const [error, setError] = useState("");
  const [connected, setConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { actions } = useZStore();

  // Determine if this is the "connected but no data" scenario
  const isNoDataScenario = reason === "no-data";

  return (
    <div
      className={cn(
        "min-h-[331px] md:min-h-min w-[262px] overflow-hidden rounded-[20px] bg-white md:h-[233px] md:w-[549px] xl:h-[351px] xl:w-[1116px]",
        "px-[35px] py-[26px] -ml-10 sm:ml-0",
        "relative",
      )}
    >
      {/* <div
        className="absolute right-5 top-5 size-[9.5px]  cursor-pointer rounded-[3px] border-[1.6px] bg-[#F9FAFB]  p-[0.5px] xl:size-[13px] xl:rounded-[5px] xl:p-px"
        onClick={() => {
          console.log("close clicked", data);
          actions.setMailchimpApiKeyModalClosed(true);
          actions.closeModal();
        }}
      >
        <IoClose className="size-full text-kd-lte-blue" />
      </div> */}
      <button
        onClick={() => {
          actions.setMailchimpApiKeyModalClosed(true);
          actions.closeModal();
        }}
        className="absolute right-5 top-5 cursor-pointer hover:opacity-60"
        aria-label="Close success message"
        type="button"
      >
        <Image src={CloseIcon} alt="close icon" />
      </button>
      <div className="flex h-full flex-col  md:flex-row">
        <div className=" flex grow flex-col items-center justify-center text-black">
          <div className="flex max-w-[581.95px] flex-col items-center text-center md:items-start md:text-start">
            <TextComponent
              type="h2"
              className="pr-2 text-left text-kd-secondary-1 md:w-full xl:mb-[6px]"
            >
              {isNoDataScenario ? "No Mailchimp Data Found" : "Setup Integration"}
            </TextComponent>

            {isNoDataScenario ? (
              <>
                <TextComponent
                  type="body"
                  className="w-full pt-2 text-left text-kd-secondary-3 xl:pt-0"
                >
                  Your connected Mailchimp account has no campaigns or data yet.
                  Please either{" "}
                  <a
                    href="https://mailchimp.com/help/create-a-regular-email-campaign/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer text-kd-lte-blue"
                  >
                    add campaigns in Mailchimp
                  </a>{" "}
                  or connect a different account that already has data.
                </TextComponent>
                <Link
                  href="/dashboard/settings/integrations"
                  onClick={() => {
                    actions.setMailchimpApiKeyModalClosed(true);
                    actions.closeModal();
                  }}
                  className="mt-2 text-kd-lte-blue hover:underline"
                >
                  <TextComponent type="body" className="text-kd-lte-blue">
                    Manage your integrations →
                  </TextComponent>
                </Link>
              </>
            ) : (
              <TextComponent
                type="body"
                className="w-full pt-2 text-left text-kd-secondary-3 xl:pt-0"
              >
                Connect your Mailchimp account by entering your
                <a
                  href="https://admin.mailchimp.com/account/api/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="cursor-pointer text-kd-lte-blue">
                    {` generate an API key `}
                  </span>
                  <div className="md:hidden">{"\n"}</div>
                </a>
                and unlock seamless integration to maximize your email marketing
                performance.
              </TextComponent>
            )}

            <div className="">
              <MailchimpForm
                setError={setError}
                setConnected={setConnected}
                connected={connected}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                initialApiKey={initialApiKey}
              />
            </div>
          </div>
        </div>
        <div className="mt-[30px] bg-white md:mt-0 md:w-[162px] xl:w-[372px]">
          <div className="flex h-full flex-col items-center justify-center px-[19px] text-center md:px-[14px] xl:px-10">
            <div className={cn(
              "flex size-10 items-center justify-center rounded-full xl:size-20",
              isNoDataScenario ? "bg-[#FEF3C7]" : "bg-[#FFDD22]"
            )}>
              <FaMailchimp className="size-full p-1" />
            </div>

            <TextComponent
              type="h3"
              className="mt-2.5 font-semibold text-kd-secondary-1 xl:mt-[9.5px]"
            >
              {isNoDataScenario ? "Account Connected" : "Connect Mailchimp"}
            </TextComponent>

            <TextComponent type="body" className="mt-[5px] text-kd-secondary-3">
              {isNoDataScenario
                ? "Your account is connected but has no campaign data available."
                : "Integrate Mailchimp to coordinate cross-channel email campaigns."}
            </TextComponent>

            <TextComponent
              type="caption"
              className={`mt-[10px] inline-flex items-center ${connected
                ? "text-[#00E096]"
                : error
                  ? "text-[#FA5A7D]"
                  : isLoading
                    ? "text-kd-lte-blue"
                    : isNoDataScenario
                      ? "text-[#F59E0B]"
                      : ""
                }`}
            >
              {connected ? (
                <>
                  <Image alt="" src={ConnectedCheckIcon} className="mr-[7px]" />
                  Connected
                </>
              ) : error ? (
                <div className="flex">
                  <IoCloseCircleOutline className="text-sm xl:text-xl" />
                  <span className="ml-1">{error}</span>
                </div>
              ) : isLoading ? (
                <>
                  <FiClock className="text-sm xl:text-xl" />
                  <span className="ml-1">Connecting...</span>
                </>
              ) : isNoDataScenario ? (
                <span className="text-[#F59E0B]">No data available</span>
              ) : null}
            </TextComponent>
          </div>
        </div>
      </div>
    </div>
  );
};

type FormEvent = React.FormEvent<HTMLFormElement>;

interface MailchimpFormProps {
  connected: boolean;
  setError: (error: string) => void;
  setConnected: (connected: boolean) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  initialApiKey?: string;
}

const MailchimpForm = ({ connected, setError, setIsLoading, initialApiKey }: MailchimpFormProps) => {
  const { data, actions } = useZStore();

  // Try to get the token from props first, then from Zustand store (connected key)
  const getInitialToken = () => {
    if (initialApiKey) return initialApiKey;
    // Fallback: check if there's a connected key in the store (from OAuth flow)
    const connectedKey = data.mailchimpProfile?.mailchimpKeys?.find(
      (key: any) => key.connected
    );
    return connectedKey?.mailchimpKey || "";
  };

  const [apiKey, setApiKey] = useState(getInitialToken);
  const [isInvalidApiKey, setIsInvalidApiKey] = useState(false);

  // Update apiKey if initialApiKey changes or mailchimpProfile is loaded with a connected key
  useEffect(() => {
    const token = getInitialToken();
    if (token && token !== apiKey) {
      setApiKey(token);
    }
  }, [initialApiKey, data.mailchimpProfile?.mailchimpKeys]);

  // MailchimpForm component
  // In MailchimpForm.tsx
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    resetState();

    try {
      if (!(await validateApiKeyAndShowError(apiKey))) return;

      const mailchimpKeyId = await setupMailchimpWithKey(apiKey);
      if (!mailchimpKeyId) {
        showToast("Something went wrong", "error");
        return;
      }

      actions.updateProcessingStatus({
        status: "processing",
        progress: 0,
        currentStep: "Starting Mailchimp data processing",
      });

      const response = await axios.post("/api/mailchimp/fetch-and-process", {
        email: data?.userDetails?.email,
        mailchimpKeyId,
      });

      const trackerId = response.data.trackerId;

      actions.updateProcessingStatus({
        status: "processing",
        progress: 1,
        trackerId,
      });
      closeModalWithDelay();
    } catch (error) {
      handleError(error);
    }
  };

  // Reset initial state
  const resetState = () => {
    setIsInvalidApiKey(false);
    setIsLoading(true);
    setError("");
  };

  // Validate the API key and show an error if invalid
  // isFromOAuth flag indicates if this is an OAuth token (from Mailchimp OAuth signup)
  const validateApiKeyAndShowError = async (apiKey: string) => {
    const isFromOAuth = !!initialApiKey; // If we have an initial API key, it's from OAuth
    const result = await validateApiKey(apiKey, isFromOAuth);
    if (!result.isValid) {
      result.errorType === "invalid" && setIsInvalidApiKey(true);
      setError(
        result.errorType === "api_down"
          ? "Mailchimp is currently unavailable, please try again later"
          : "Not connected",
      );
      setIsLoading(false);
    }
    return result.isValid;
  };

  // Set up Mailchimp and return the Mailchimp Key ID if successful
  const setupMailchimpWithKey = async (apiKey: string) => {
    const mailchimpKeyId = crypto.randomUUID();
    actions.addMailchimpApiKey("default", apiKey, mailchimpKeyId);
    actions.setSelectedMailchimpApiKey(mailchimpKeyId);

    const res = await setupMailchimp({
      mailchimpAccessTokenString: apiKey,
      email: data?.userDetails?.email || "",
      mailchimpKeyId,
    });

    // if (res?.success) setConnected(true);
    return res?.success ? mailchimpKeyId : null;
  };

  // Show toast notifications
  const showToast = (message: string, type: "success" | "error") => {
    toast[type](message, { position: "bottom-right" });
  };

  // Close modal with a delay
  const closeModalWithDelay = () => {
    setTimeout(() => actions.closeModal(), 2000);
  };

  // Handle errors
  const handleError = (error: any) => {
    console.error(error);
    setError("Something went wrong");
    setIsInvalidApiKey(true);
  };

  const validateApiKey = async (key: string, isFromOAuth: boolean = false) => {
    // For OAuth tokens, we skip regex validation since:
    // 1. OAuth tokens have a different format than traditional API keys
    // 2. They've already been validated during the OAuth callback
    // We still check that the key contains the server prefix (has a dash)
    if (!isFromOAuth) {
      const apiKeyPattern = /^[a-f0-9]{32}-us\d{1,2}$/;
      if (!apiKeyPattern.test(key))
        return { isValid: false, errorType: "invalid" };
    } else {
      // For OAuth tokens, just verify it has the expected format with server suffix
      if (!key.includes("-")) {
        return { isValid: false, errorType: "invalid" };
      }
    }

    const [token, server] = key.split("-");
    try {
      await axios.get(
        `/api/mailchimp/ping?apiKey=${encodeURIComponent(token)}&server=${encodeURIComponent(server)}`,
      );
      return { isValid: true };
    } catch (error) {
      let errorType: "invalid" | "api_down" = "invalid";

      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Check for nested Mailchimp 503 error
          const mailchimpStatus = error.response.data?.error?.status;
          if (mailchimpStatus === 503) {
            errorType = "api_down";
          }
          // Check for 5xx errors from the proxy
          else if (error.response.status >= 500) {
            errorType = "api_down";
          }
        } else {
          // Network errors or no response
          errorType = "api_down";
        }
      } else if (
        error instanceof Error &&
        error.message.includes("Network Error")
      ) {
        errorType = "api_down";
      }

      console.error("Mailchimp API validation error:", error);
      return { isValid: false, errorType };
    }
  };

  return (
    <div className="relative">
      <form onSubmit={handleSubmit}>
        {isInvalidApiKey && (
          <TextComponent
            type="body"
            className="absolute my-[9px] text-[#FA5A7D]"
          >
            *Please enter a valid API key
          </TextComponent>
        )}

        <TextComponent type="body">
          <input
            disabled={connected}
            type="text"
            value={apiKey}
            placeholder="Enter your Mailchimp API Key"
            className={cn(
              "xl:mt-[33px]  mt-[29px] md:mt-[34px] xl:h-[60px] xl:w-[592.14px] xl:rounded-[16px] bg-[#F9FAFB] px-[24px]",
              "h-[30px] md:w-[248px] w-[168px]",
              "rounded-[8px] xl:rounded-[10.67px]",
              // "2xl:text-[16px] xl:text-[10.5px] text-[9px]",
              isInvalidApiKey ? "border-2 border-[#FA5A7D]" : "",
              "w-[204px] md:w-[280px] xl:w-[592.14px]",
            )}
            onChange={(e) => setApiKey(e.target.value.trim())}
          />
        </TextComponent>
        <div className="mt-[14px] flex flex-col gap-3 xl:mt-[27px] md:flex-row md:items-center">
          <Button
            disabled={connected}
            type="submit"
            className={cn(
              "bg-kd-lte-blue px-[23px] text-white w-[204px]",
              connected &&
              "bg-kd-secondary-5 text-white hover:bg-kd-secondary-5 hover:text-white border-none cursor-not-allowed",
            )}
          >
            <TextComponent type="button">Continue</TextComponent>
          </Button>
          {!connected && (
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-kd-secondary-3">
                or
              </span>
              <MailchimpConnectButton
                mode="integration"
                withLabel
                onIntegrationSuccess={async ({ keyId, skipRefresh }) => {
                  toast.success("Mailchimp account connected successfully!", { position: "bottom-right" });
                  actions.setMailchimpApiKeyModalClosed(true);
                  actions.closeModal();
                  if (!skipRefresh) {
                    actions.updateProcessingStatus({
                      status: "processing",
                      progress: 0,
                      currentStep: "Starting Mailchimp data processing",
                    });
                    try {
                      const response = await axios.post("/api/mailchimp/fetch-and-process", {
                        email: data?.userDetails?.email,
                        mailchimpKeyId: keyId,
                        getAllTimeData: true,
                      });
                      actions.updateProcessingStatus({
                        status: "processing",
                        progress: 1,
                        trackerId: response.data.trackerId,
                        mailchimpKeyId: keyId,
                      });
                    } catch (err) {
                      console.error("Error triggering data processing:", err);
                      actions.updateProcessingStatus({
                        status: "failed",
                        progress: 0,
                        error: "Failed to start data processing",
                      });
                    }
                  }
                }}
                onError={(errorCode) => {
                  if (errorCode === "popup_closed") return;
                  const msg =
                    errorCode === "popup_blocked"
                      ? "Pop-up was blocked. Please allow pop-ups and try again."
                      : errorCode === "timeout"
                        ? "Mailchimp connection timed out. Please try again."
                        : `Mailchimp connection failed (${errorCode}).`;
                  toast.error(msg, { position: "bottom-right" });
                }}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default MailchimpAPISettingsModal;
